import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { LanguageProvider } from "@/hooks/useLanguage";
import { CartProvider } from "@/hooks/useCart";
import Index from "./pages/Index.tsx";
import ScrollToTop from "./components/ScrollToTop.tsx";

// Lazy load non-critical routes
const ParMums = lazy(() => import("./pages/ParMums.tsx"));
const Kondicionieri = lazy(() => import("./pages/Kondicionieri.tsx"));
const Apkure = lazy(() => import("./pages/Apkure.tsx"));
const Ventilacija = lazy(() => import("./pages/Ventilacija.tsx"));
const Projektesana = lazy(() => import("./pages/Projektesana.tsx"));
const Serviss = lazy(() => import("./pages/Serviss.tsx"));
const Konsultacijas = lazy(() => import("./pages/Konsultacijas.tsx"));
const AdminLogin = lazy(() => import("./pages/AdminLogin.tsx"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard.tsx"));
const Projekti = lazy(() => import("./pages/Projekti.tsx"));
const ProjektaLapa = lazy(() => import("./pages/ProjektaLapa.tsx"));
const Kontakti = lazy(() => import("./pages/Kontakti.tsx"));
const Pakalpojumi = lazy(() => import("./pages/Pakalpojumi.tsx"));
const IndustrialieObjekti = lazy(() => import("./pages/IndustrialieObjekti.tsx"));
const Lielveikali = lazy(() => import("./pages/Lielveikali.tsx"));
const Razotnes = lazy(() => import("./pages/Razotnes.tsx"));
const Privatmajas = lazy(() => import("./pages/Privatmajas.tsx"));
const Servertelpas = lazy(() => import("./pages/Servertelpas.tsx"));

const NotFound = lazy(() => import("./pages/NotFound.tsx"));

const queryClient = new QueryClient();

/**
 * Public routes, rendered once at "/" for Latvian and once under "/en" for
 * English, so each language has its own indexable URL.
 */
const publicRoutes: { path: string; element: JSX.Element }[] = [
  { path: "", element: <Index /> },
  { path: "pakalpojumi", element: <Pakalpojumi /> },
  { path: "par-mums", element: <ParMums /> },
  { path: "kondicionieri", element: <Kondicionieri /> },
  { path: "apkure", element: <Apkure /> },
  { path: "ventilacija", element: <Ventilacija /> },
  { path: "projektesana", element: <Projektesana /> },
  { path: "serviss", element: <Serviss /> },
  { path: "konsultacijas", element: <Konsultacijas /> },
  { path: "kontakti", element: <Kontakti /> },
  { path: "projekti", element: <Projekti /> },
  { path: "projekti/:slug", element: <ProjektaLapa /> },
  { path: "industrialie-objekti", element: <IndustrialieObjekti /> },
  { path: "lielveikali", element: <Lielveikali /> },
  { path: "razotnes", element: <Razotnes /> },
  { path: "privatmajas", element: <Privatmajas /> },
  { path: "servertelpas", element: <Servertelpas /> },
];

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <CartProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <LanguageProvider>
              <ScrollToTop />
              <Suspense fallback={
                <div className="min-h-screen flex items-center justify-center">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              }>
                <Routes>
                  {publicRoutes.map(({ path, element }) => (
                    <Route key={`lv-${path}`} path={`/${path}`} element={element} />
                  ))}
                  {publicRoutes.map(({ path, element }) => (
                    <Route key={`en-${path}`} path={`/en/${path}`} element={element} />
                  ))}

                  {/* Admin is internal — Latvian only, and noindexed */}
                  <Route path="/admin" element={<AdminLogin />} />
                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </LanguageProvider>
          </BrowserRouter>
        </TooltipProvider>
      </CartProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
